// LEGACY: Uses SCSS with CSS modules. New code should use @metarouter-private/mr-mui components instead.
// LEGACY: Uses @ion/components. New code should use @metarouter-private/mr-mui components instead.
import React, { useState, useEffect } from 'react';
import pt from 'prop-types';
import cx from 'classnames';
import { useHistory } from 'react-router-dom';

import { Box, Typography, ConfirmDelete, Chip } from '@metarouter-private/mr-mui';

import { MrCard, MrDropdown, MrTooltipDeprecated } from '@ion/components';
import { useRemoveIntegration, useQueryPipelines, useCloneIntegration } from '@ion/api';

import IntegrationLogo from '../../components/integrations/integration-logo';
import hasPipelineIntegrations from './has-pipeline-integrations';

import CloneIntegrationModal from 'app/integrations/CloneIntegrationModal';
import Revision from 'app/components/revision';
import { routes } from 'app/constants';
import StarterKits from '../../starterkits/starterkits-state';

import s from '../index.module.scss';

export default function IntegrationCard({ integration }) {
  const history = useHistory();
  const [hasPipelineIntegration, setHasPipelineIntegration] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [removeIntegration, { isAllowed: deleteIntegrationPermission }] = useRemoveIntegration();
  const [, { isAllowed: cloneIntegrationPermission }] = useCloneIntegration();
  const { data: pipelines } = useQueryPipelines();
  const { kits, kitById } = StarterKits();
  const kit = kitById(kits, integration?.starterkitId);
  const isBeta = kit?.metadata?.isBeta;

  useEffect(() => {
    const hasPipelineIntegration = hasPipelineIntegrations(integration.id, pipelines);

    if (hasPipelineIntegration) {
      setHasPipelineIntegration(hasPipelineIntegration);
    }
  }, [pipelines, integration.id]);

  return (
    <>
      <MrCard
        className={cx(s.card, s.integrationCard)}
        styleNames="link"
        onClick={() => history.push(routes.editPlaybook, { integrationId: integration.id })}
      >
        {(deleteIntegrationPermission || cloneIntegrationPermission) && (
          <MrDropdown className={s.cardDropdown}>
            <ul>
              {cloneIntegrationPermission && (
                <li
                  onClick={event => {
                    event.stopPropagation();
                    setCloneModalOpen(true);
                  }}
                >
                  Clone
                </li>
              )}
              {deleteIntegrationPermission && (
                <li
                  onClick={event => {
                    event.stopPropagation();
                    setDeleteModalOpen(true);
                  }}
                >
                  Delete
                </li>
              )}
            </ul>
          </MrDropdown>
        )}

        <IntegrationLogo id={integration.starterkitId} className={s.logo} />

        {/* TECH DEBT: Using sx as a stop gap to reduce scope of refactor. Should use other layout component instead. */}
        <Box sx={{ overflow: 'hidden' }}>
          <Box sx={{ position: 'relative', top: '3px' }}>
            <Typography variant="h5" ellipsis title={integration.name}>
              {integration.name}
            </Typography>
          </Box>

          <Revision revision={integration.playbooks[0]?.revision} type="Revision" />
        </Box>

        {isBeta && (
          <Box pl={1} display="flex" alignItems="center">
            <MrTooltipDeprecated className={s.tooltip} text="In Beta - avoid using in production, some bugs may occur.">
              <Chip label="BETA" size="small" variant="outlined" color="secondary" />
            </MrTooltipDeprecated>
          </Box>
        )}
      </MrCard>

      {cloneModalOpen && (
        <CloneIntegrationModal
          open={cloneModalOpen}
          integration={integration}
          onClose={() => setCloneModalOpen(false)}
        />
      )}

      <ConfirmDelete
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={`Delete ${integration.name}?`}
        message={<DeleteIntegrationMessage name={integration.name} hasPipelineIntegration={hasPipelineIntegration} />}
        onConfirm={() => {
          removeIntegration({ integrationId: integration.id, name: integration.name });
          setDeleteModalOpen(false);
        }}
        ConfirmProps={{ disabled: hasPipelineIntegration, 'data-test-id': 'ConfirmDeleteIntegration' }}
      />
    </>
  );
}

IntegrationCard.propTypes = {
  integration: pt.object,
};

function DeleteIntegrationMessage({ name, hasPipelineIntegration }) {
  if (hasPipelineIntegration) {
    return (
      <Typography>
        Unable to delete integrations that are attached to pipelines. Please remove this integration from all pipelines
        first and return to delete.
      </Typography>
    );
  }

  return (
    <Typography>
      Deleting the <strong>{name}</strong> integration cannot be undone, and all data, including revisions, will be
      lost.
    </Typography>
  );
}

DeleteIntegrationMessage.propTypes = {
  name: pt.string.isRequired,
  hasPipelineIntegration: pt.bool,
};
